import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { FileText, Search, ExternalLink, Trash2, Plus, Edit, ArrowLeft, Sparkles, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { feedback } from "@/lib/feedback";
import SignaturePad from "@/components/SignaturePad";
import { PhotoCapture } from "@/components/PhotoCapture";

const ALMACENISTA_OPCIONES = ["CARLOS CANIZALES", "CYNTHIA SAUCEDA"];
const GUARDIA_OPCIONES = ["MARIO AGUILAR", "ADELAIDO SAENZ"];

const EMPTY_TICKET = {
  almacenista: "",
  area: "",
  sellos: "",
  cliente: "",
  operador: "",
  linea_transporte: "",
  numero_economico: "",
  placas_unidad: "",
  numero_caja: "",
  placas_caja: "",
  hora_llegada: "",
  hora_apertura_cortina: "",
  hora_cierre_cortina: "",
  hora_salida: "",
  numero_pallets: "",
  numero_sello: "",
  numero_orden_compra: "",
  observaciones: "",
  dano_caja: "",
  nombre_guardia: "",
  firma_almacenista: "",
  firma_guardia: "",
  foto_inicio_carga: "",
  foto_media_carga: "",
  foto_final_carga: "",
};

export default function SirucTickets() {
  const [search, setSearch] = useState("");
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_TICKET);
  const [almacenistaOtro, setAlmacenistaOtro] = useState(false);
  const [guardiaOtro, setGuardiaOtro] = useState(false);

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["siruc-tickets"],
    queryFn: () => api.get("/siruc/tickets").then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post("/siruc/tickets", body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["siruc-tickets"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Ticket de embarque creado");
      feedback.success();
      setShowForm(false);
      setForm(EMPTY_TICKET);
      setAlmacenistaOtro(false);
      setGuardiaOtro(false);
    },
    onError: (e) => { toast.error(apiError(e, "Error al crear ticket")); feedback.error(); },
  });

  const updateMutation = useMutation({
    mutationFn: (body) => api.patch(`/siruc/tickets/${editItem.id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["siruc-tickets"] });
      toast.success("Ticket actualizado");
      setEditItem(null);
    },
    onError: (e) => toast.error(apiError(e, "Error al actualizar")),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/siruc/tickets/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["siruc-tickets"] });
      toast.success("Ticket eliminado");
    },
    onError: (e) => toast.error(apiError(e, "Error al eliminar")),
  });

  const ocrMutation = useMutation({
    mutationFn: (body) => api.post("/ocr/analyze", body).then(r => r.data),
    onSuccess: (raw) => {
      const data = {};
      Object.entries(raw || {}).forEach(([k, v]) => { data[String(k).trim().toLowerCase()] = v; });
      setForm(prev => ({
        ...prev,
        placas_unidad: data.placas_unidad ? String(data.placas_unidad).toUpperCase() : prev.placas_unidad,
        cliente: data.cliente ? String(data.cliente).toUpperCase() : prev.cliente,
        operador: data.operador ? String(data.operador).toUpperCase() : prev.operador,
        numero_caja: data.numero_caja ? String(data.numero_caja).toUpperCase() : prev.numero_caja,
        numero_pallets: data.numero_pallets ? String(data.numero_pallets) : prev.numero_pallets,
        numero_sello: data.numero_sello ? String(data.numero_sello).toUpperCase() : prev.numero_sello,
      }));
      toast.success("Datos del ticket extraídos");
    },
    onError: (e) => toast.error(apiError(e, "Error al analizar el ticket")),
  });

  const handleScan = (img) => ocrMutation.mutate({ image_b64: img, context: "ticket" });

  // Helper para buscar cualquier valor que se parezca a lo que buscamos
  const findVal = (obj, patterns) => {
    for (const p of patterns) {
      const match = Object.keys(obj).find(k => k.toLowerCase().includes(p.toLowerCase()));
      if (match && obj[match]) return obj[match];
    }
    return null;
  };

  const filtered = (tickets || []).filter(t => {
    const searchStr = search.toLowerCase();
    const ticketVal = String(findVal(t, ["ticket", "folio", "guia", "id"]) || "").toLowerCase();
    const clientVal = String(findVal(t, ["client", "proveedor", "nombre", "customer"]) || "").toLowerCase();
    return ticketVal.includes(searchStr) || clientVal.includes(searchStr);
  });

  const handleEdit = (ticket) => {
    setEditItem(ticket);
    setEditForm({ ...ticket });
  };

  const canSubmit = form.almacenista.trim() && form.cliente.trim() && form.placas_unidad.trim();

  const submitNew = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Almacenista, cliente y placas son obligatorios");
      return;
    }
    createMutation.mutate({
      ...form,
      cliente: form.cliente.trim().toUpperCase(),
      operador: form.operador.trim().toUpperCase(),
      linea_transporte: form.linea_transporte.trim().toUpperCase(),
      placas_unidad: form.placas_unidad.trim().toUpperCase(),
      numero_caja: form.numero_caja.trim().toUpperCase(),
      placas_caja: form.placas_caja.trim().toUpperCase(),
      observaciones: form.observaciones.trim().toUpperCase(),
    });
  };

  if (showForm) {
    return (
      <div className="p-0 sm:p-4 lg:p-8 max-w-4xl mx-auto bg-secondary/30 min-h-screen">
        <div className="sticky top-0 z-20 bg-white border-b border-border p-4 flex items-center justify-between shadow-sm">
          <button onClick={() => setShowForm(false)} className="flex items-center gap-2 text-navy font-bold uppercase text-xs">
            <ArrowLeft size={18} /> Volver
          </button>
          <h1 className="font-heading text-lg font-black text-navy uppercase tracking-tight">Nuevo Ticket de Embarque</h1>
          <div className="w-10"></div>
        </div>

        <div className="p-4 space-y-6 pb-24">
          <section className="bg-white border border-border p-4 rounded-xl shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-navy text-white rounded-lg flex items-center justify-center">
                <Sparkles size={18} />
              </div>
              <div>
                <h2 className="font-heading font-bold text-navy leading-none">Auto-llenado Inteligente</h2>
                <p className="text-[10px] text-muted-foreground uppercase font-black mt-1">Escanea el ticket físico</p>
              </div>
            </div>
            <PhotoCapture single label="Escaneo de ticket (Cámara o Galería)" onChange={(v) => handleScan(v[0])} testid="siruc-ticket-scan" />
            {ocrMutation.isPending && (
              <div className="mt-3 p-3 bg-navy/5 border border-navy/20 rounded-lg flex items-center gap-3">
                <RefreshCw size={16} className="text-navy animate-spin" />
                <span className="text-xs font-bold text-navy animate-pulse">Analizando ticket físico...</span>
              </div>
            )}
          </section>

          <form onSubmit={submitNew} className="space-y-6">
            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Almacén</p>
              </div>
              <div className="p-4 space-y-4">
                <Field label="Almacenista *">
                  {!almacenistaOtro ? (
                    <select
                      value={ALMACENISTA_OPCIONES.includes(form.almacenista) ? form.almacenista : ""}
                      onChange={(e) => {
                        if (e.target.value === "__otro__") { setAlmacenistaOtro(true); setForm({ ...form, almacenista: "" }); }
                        else setForm({ ...form, almacenista: e.target.value });
                      }}
                      className="input h-12 bg-secondary/20 border-none"
                    >
                      <option value="" disabled>Selecciona...</option>
                      {ALMACENISTA_OPCIONES.map((a) => <option key={a} value={a}>{a}</option>)}
                      <option value="__otro__">Otro...</option>
                    </select>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        value={form.almacenista}
                        onChange={(e) => setForm({ ...form, almacenista: e.target.value })}
                        className="input h-12 bg-secondary/20 border-none flex-1"
                        placeholder="Nombre del almacenista"
                      />
                      <button type="button" onClick={() => setAlmacenistaOtro(false)} className="text-[10px] font-black uppercase text-navy/60">Lista</button>
                    </div>
                  )}
                </Field>
                <Field label="Área">
                  <input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                </Field>
              </div>
            </div>

            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Cliente y Transporte</p>
              </div>
              <div className="p-4 space-y-4">
                <Field label="Cliente *">
                  <input value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Operador">
                    <input value={form.operador} onChange={(e) => setForm({ ...form, operador: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                  </Field>
                  <Field label="Línea de Transporte">
                    <input value={form.linea_transporte} onChange={(e) => setForm({ ...form, linea_transporte: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Placas Unidad *">
                    <input value={form.placas_unidad} onChange={(e) => setForm({ ...form, placas_unidad: e.target.value })} className="input h-12 bg-secondary/20 border-none uppercase font-bold" />
                  </Field>
                  <Field label="Número Económico">
                    <input value={form.numero_economico} onChange={(e) => setForm({ ...form, numero_economico: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Número de Caja">
                    <input value={form.numero_caja} onChange={(e) => setForm({ ...form, numero_caja: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                  </Field>
                  <Field label="Placas de Caja">
                    <input value={form.placas_caja} onChange={(e) => setForm({ ...form, placas_caja: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                  </Field>
                </div>
              </div>
            </div>

            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Carga</p>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Número de Pallets">
                    <input value={form.numero_pallets} onChange={(e) => setForm({ ...form, numero_pallets: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                  </Field>
                  <Field label="Número de Sello">
                    <input value={form.numero_sello} onChange={(e) => setForm({ ...form, numero_sello: e.target.value })} className="input h-12 bg-secondary/20 border-none font-bold" />
                  </Field>
                </div>
                <Field label="Sellos (detalle)">
                  <input value={form.sellos} onChange={(e) => setForm({ ...form, sellos: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                </Field>
                <Field label="Orden de Compra">
                  <input value={form.numero_orden_compra} onChange={(e) => setForm({ ...form, numero_orden_compra: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                </Field>
                <Field label="Daño en Caja">
                  <input value={form.dano_caja} onChange={(e) => setForm({ ...form, dano_caja: e.target.value })} className="input h-12 bg-secondary/20 border-none" placeholder="Describe si hay daño visible" />
                </Field>
                <Field label="Observaciones">
                  <textarea value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} className="input min-h-[70px] bg-secondary/20 border-none" />
                </Field>
              </div>
            </div>

            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Horarios</p>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <Field label="Hora de Llegada">
                  <input type="time" value={form.hora_llegada} onChange={(e) => setForm({ ...form, hora_llegada: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                </Field>
                <Field label="Hora de Salida">
                  <input type="time" value={form.hora_salida} onChange={(e) => setForm({ ...form, hora_salida: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                </Field>
                <Field label="Apertura de Cortina">
                  <input type="time" value={form.hora_apertura_cortina} onChange={(e) => setForm({ ...form, hora_apertura_cortina: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                </Field>
                <Field label="Cierre de Cortina">
                  <input type="time" value={form.hora_cierre_cortina} onChange={(e) => setForm({ ...form, hora_cierre_cortina: e.target.value })} className="input h-12 bg-secondary/20 border-none" />
                </Field>
              </div>
            </div>

            <div className="bg-white border border-border rounded-xl p-4 shadow-sm space-y-4">
              <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Fotografías de Carga</p>
              <PhotoCapture label="Inicio de Carga" single value={form.foto_inicio_carga ? [form.foto_inicio_carga] : []} onChange={(v) => setForm({ ...form, foto_inicio_carga: v[0] || "" })} testid="siruc-ticket-foto-inicio" />
              <PhotoCapture label="Media Carga" single value={form.foto_media_carga ? [form.foto_media_carga] : []} onChange={(v) => setForm({ ...form, foto_media_carga: v[0] || "" })} testid="siruc-ticket-foto-media" />
              <PhotoCapture label="Final de Carga" single value={form.foto_final_carga ? [form.foto_final_carga] : []} onChange={(v) => setForm({ ...form, foto_final_carga: v[0] || "" })} testid="siruc-ticket-foto-final" />
            </div>

            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Guardia de Seguridad</p>
              </div>
              <div className="p-4 space-y-4">
                <Field label="Nombre del Guardia">
                  {!guardiaOtro ? (
                    <select
                      value={GUARDIA_OPCIONES.includes(form.nombre_guardia) ? form.nombre_guardia : ""}
                      onChange={(e) => {
                        if (e.target.value === "__otro__") { setGuardiaOtro(true); setForm({ ...form, nombre_guardia: "" }); }
                        else setForm({ ...form, nombre_guardia: e.target.value });
                      }}
                      className="input h-12 bg-secondary/20 border-none"
                    >
                      <option value="" disabled>Selecciona...</option>
                      {GUARDIA_OPCIONES.map((g) => <option key={g} value={g}>{g}</option>)}
                      <option value="__otro__">Otro...</option>
                    </select>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input autoFocus value={form.nombre_guardia} onChange={(e) => setForm({ ...form, nombre_guardia: e.target.value })} className="input h-12 bg-secondary/20 border-none flex-1" />
                      <button type="button" onClick={() => setGuardiaOtro(false)} className="text-[10px] font-black uppercase text-navy/60">Lista</button>
                    </div>
                  )}
                </Field>
              </div>
            </div>

            <div className="bg-white border border-border rounded-xl p-4 shadow-sm">
              <div className="grid md:grid-cols-2 gap-6">
                <SignaturePad label="Firma del Almacenista *" value={form.firma_almacenista} onChange={(v) => setForm({ ...form, firma_almacenista: v })} testid="siruc-ticket-firma-almacenista" />
                <SignaturePad label="Firma del Guardia *" value={form.firma_guardia} onChange={(v) => setForm({ ...form, firma_guardia: v })} testid="siruc-ticket-firma-guardia" />
              </div>
            </div>

            <button
              type="submit"
              disabled={createMutation.isPending || !canSubmit}
              className="w-full h-14 bg-navy text-white font-black uppercase tracking-widest rounded-xl shadow-lg active:scale-95 transition-all disabled:opacity-50"
            >
              {createMutation.isPending ? "Procesando..." : !canSubmit ? "Completa los campos obligatorios (*)" : "Registrar Ticket"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <header className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight text-navy">SRIUC · Tickets de Embarque</h1>
          <p className="text-sm text-muted-foreground mt-1">Registros de carga, propios y sincronizados desde el sistema central.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
        >
          <Plus size={18} /> Nuevo ticket
        </button>
      </header>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <input
          type="text"
          placeholder="Buscar por ticket, cliente o proveedor..."
          className="input pl-10 h-11"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando tickets...</p>
      ) : (
        <div className="bg-white border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary border-b border-border">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">No. Ticket</th>
                  <th className="px-4 py-3 font-medium">Tipo</th>
                  <th className="px-4 py-3 font-medium">Cliente / Proveedor</th>
                  <th className="px-4 py-3 font-medium">Pallets</th>
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((t) => {
                  const ticketNo = findVal(t, ["ticket", "folio", "guia", "reference"]) || t.id?.substring(0, 8);
                  const client = findVal(t, ["client", "proveedor", "nombre", "customer", "target", "cliente"]);
                  const type = findVal(t, ["type", "tipo", "operation"]) || "embarque";
                  const pallets = findVal(t, ["pallet", "bulto", "cantidad", "count"]) || 0;
                  const recordId = t.record_id || t.vehicle_record_id || findVal(t, ["record", "parent"]);

                  return (
                    <tr key={t.id} className="hover:bg-secondary/50">
                      <td className="px-4 py-3 font-bold text-navy">{ticketNo}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${
                          String(type).toLowerCase().includes('embarque') ? 'bg-navy text-white' : 'bg-success text-white'
                        }`}>
                          {type}
                        </span>
                      </td>
                      <td className="px-4 py-3">{client || "—"}</td>
                      <td className="px-4 py-3">{pallets}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {t.created_at ? new Date(t.created_at).toLocaleString() : (t._synced_at ? new Date(t._synced_at).toLocaleString() : "—")}
                      </td>
                      <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
                        {isAdmin && (
                          <button onClick={() => handleEdit(t)} className="p-2 inline-block text-navy hover:bg-navy/5 rounded-full" title="Editar ticket">
                            <Edit size={16} />
                          </button>
                        )}
                        {recordId && (
                          <a href={`/siruc/records/${recordId}`} className="p-2 inline-block text-navy hover:bg-navy/5 rounded-full" title="Ver registro vinculado">
                            <ExternalLink size={16} />
                          </a>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => { if (confirm("¿Eliminar este ticket?")) deleteMutation.mutate(t.id); }}
                            className="p-1.5 text-critical hover:bg-critical/10 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">No se encontraron tickets.</div>
          )}
        </div>
      )}

      {editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-navy">Modificar Ticket</h3>
              <button onClick={() => setEditItem(null)} className="p-2 hover:bg-secondary">
                <ArrowLeft size={18} />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateMutation.mutate(editForm);
              }}
              className="space-y-4"
            >
              <div className="grid md:grid-cols-2 gap-4">
                {Object.keys(editForm).filter(k => !k.startsWith('_') && k !== 'id' && !k.includes('firma') && !k.includes('foto')).map(key => (
                  <div key={key} className="space-y-1.5">
                    <label className="text-xs font-bold text-navy uppercase">{key.replace(/_/g, ' ')}</label>
                    <input
                      className="input"
                      value={editForm[key] || ""}
                      onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                    />
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-6 bg-navy/5 p-4 border border-navy/10 mt-4">
                <SignaturePad
                  label="Firma del Almacenista *"
                  value={editForm.firma_almacenista}
                  onChange={(v) => setEditForm({ ...editForm, firma_almacenista: v })}
                  testid="siruc-tkt-firma-almacenista-edit"
                />
                <SignaturePad
                  label="Firma del Guardia *"
                  value={editForm.firma_guardia}
                  onChange={(v) => setEditForm({ ...editForm, firma_guardia: v })}
                  testid="siruc-tkt-firma-guardia-edit"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="h-11 px-6 bg-navy text-white font-bold">GUARDAR CAMBIOS</button>
                <button type="button" onClick={() => setEditItem(null)} className="h-11 px-6 border border-border">CANCELAR</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-navy">{label}</label>
      {children}
    </div>
  );
}
